import React, { useState } from 'react';
import { salin } from '../services/promptArchV2Service';

// ═══════════════════════════════════════════════════════════
//  PROMPT ARCHITECTURE V2 — tab Prompt Inspector
//  Tunjuk TEPAT apa yang akan dihantar kepada OpenAI untuk pilihan semasa Playground:
//  komponen + versi, arahan (system), input (user), peta scene, amaran, anggaran token.
//  Dikompil di server (aksi `kompil`) — tiada panggilan OpenAI, tiada kos. Kunci API tidak
//  pernah sampai ke page ini.
// ═══════════════════════════════════════════════════════════

function Blok({ tajuk, teks, lalaiBuka }) {
  const [keadaan, setKeadaan] = useState(null);
  const klik = async () => {
    const ok = await salin(teks);
    setKeadaan(ok ? 'Copied' : 'Gagal salin');
    setTimeout(() => setKeadaan(null), 1500);
  };
  return (
    <details open={lalaiBuka} className="rounded-xl bg-white border border-slate-200 p-4 mb-3">
      <summary className="text-[13.5px] font-semibold text-slate-800 cursor-pointer">
        {tajuk} <span className="text-[11.5px] font-normal text-slate-400">· {String(teks || '').length.toLocaleString()} aksara</span>
      </summary>
      <div className="flex justify-end mt-2">
        <button onClick={klik} className="text-[12px] font-semibold text-indigo-600 hover:underline">{keadaan || 'Copy'}</button>
      </div>
      <pre className="text-[12px] text-slate-700 bg-slate-50 rounded-lg p-3 whitespace-pre-wrap break-words m-0 mt-1 max-h-[560px] overflow-y-auto">{teks || '(kosong)'}</pre>
    </details>
  );
}

export default function ArchV2Inspector({ kompilan, onKompil, dasar, halangan }) {
  const k = kompilan.data;
  const [salinSemua, setSalinSemua] = useState(null);
  const klikSemua = async () => {
    const ok = await salin(`===== INSTRUCTIONS (system) =====\n${k.system}\n\n===== INPUT (user) =====\n${k.user}`);
    setSalinSemua(ok ? 'Copied' : 'Gagal salin');
    setTimeout(() => setSalinSemua(null), 1500);
  };

  return (
    <div>
      <div className="rounded-xl bg-white border border-slate-200 p-5 mb-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[14px] font-semibold text-slate-800 m-0">Prompt Inspector</p>
            <p className="text-[12px] text-slate-500 m-0 mt-0.5">Apa yang akan dihantar kepada OpenAI untuk pilihan semasa di Playground. Percuma — tiada panggilan AI.</p>
          </div>
          <button
            onClick={onKompil}
            disabled={kompilan.sedang || !!halangan}
            className="h-9 flex items-center gap-2 px-4 text-[13px] font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <span className={`material-symbols-outlined text-[16px] ${kompilan.sedang ? 'animate-spin' : ''}`}>{kompilan.sedang ? 'progressive_activity' : 'refresh'}</span>
            {kompilan.sedang ? 'Mengkompil…' : (k ? 'Compile semula' : 'Compile')}
          </button>
        </div>
        {halangan && <p className="text-[12.5px] text-amber-700 m-0 mt-2">{halangan}</p>}
        {kompilan.basi && k && !kompilan.sedang && (
          <p className="text-[12.5px] text-amber-700 m-0 mt-2">Pilihan di Playground atau pustaka sudah berubah sejak kompilan ini — tekan "Compile semula".</p>
        )}
        {kompilan.ralat && (
          <div className="mt-3 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2.5 text-[12.5px] text-rose-700"><span className="font-bold">Kompilan gagal:</span> {kompilan.ralat}</div>
        )}
      </div>

      {k && (
        <>
          {k.ralat?.length > 0 && (
            <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 mb-3">
              <p className="text-[13px] font-bold text-rose-700 m-0 mb-1">Tidak boleh dijana — betulkan dahulu</p>
              <ul className="m-0 pl-4 text-[12.5px] text-rose-700 leading-relaxed list-disc">{k.ralat.map((x, i) => <li key={i}>{x}</li>)}</ul>
            </div>
          )}
          {k.amaran?.length > 0 && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 mb-3">
              <p className="text-[13px] font-bold text-amber-800 m-0 mb-1">Validation warnings</p>
              <ul className="m-0 pl-4 text-[12.5px] text-amber-800 leading-relaxed list-disc">{k.amaran.map((x, i) => <li key={i}>{x}</li>)}</ul>
            </div>
          )}

          <div className="rounded-xl bg-white border border-slate-200 p-4 mb-3 overflow-x-auto">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
              <p className="text-[13.5px] font-semibold text-slate-800 m-0">Komponen dalam prompt ini</p>
              {k.ok && (
                <p className="text-[12px] text-slate-500 m-0">
                  ≈ <b>{Number(k.anggaran?.token || 0).toLocaleString()}</b> token input ({Number(k.anggaran?.aksara || 0).toLocaleString()} aksara) — anggaran kasar aksara ÷ 4; angka sebenar dilaporkan OpenAI selepas run.
                </p>
              )}
            </div>
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="text-left text-slate-400 border-b border-slate-100">
                  <th className="py-1.5 pr-3 font-semibold">Layer</th>
                  <th className="py-1.5 pr-3 font-semibold">Component</th>
                  <th className="py-1.5 pr-3 font-semibold">Name</th>
                  <th className="py-1.5 pr-3 font-semibold">ID</th>
                  <th className="py-1.5 pr-3 font-semibold">Version</th>
                  <th className="py-1.5 font-semibold">Source</th>
                </tr>
              </thead>
              <tbody>
                {(k.komponen || []).map((c, i) => (
                  <tr key={i} className="border-b border-slate-50 text-slate-600">
                    <td className="py-1.5 pr-3 font-bold text-indigo-500">{c.lapisan}</td>
                    <td className="py-1.5 pr-3">{c.label}</td>
                    <td className="py-1.5 pr-3 font-semibold text-slate-700">{c.nama}</td>
                    <td className="py-1.5 pr-3 font-mono text-[11.5px] text-slate-400">{c.id || '—'}</td>
                    <td className="py-1.5 pr-3">{c.versi != null ? `v${c.versi}` : '—'}</td>
                    <td className="py-1.5">{c.sumber === 'benih' ? 'default' : c.sumber === 'disunting' ? 'edited' : c.sumber === 'manual' ? 'manual input' : 'library'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {k.ok && (
            <>
              <div className="rounded-xl bg-white border border-slate-200 p-4 mb-3">
                <p className="text-[13.5px] font-semibold text-slate-800 m-0 mb-1.5">Scene map (dikira oleh Scene Planner)</p>
                <ol className="m-0 pl-5 text-[12.5px] text-slate-600 leading-relaxed list-decimal">
                  {(k.petaBabak || []).map((s) => (
                    <li key={s.babak}>
                      {s.rentak.length ? s.rentak.join('  +  ') : '(tiada rentak)'}
                      {s.bahagian && <span className="text-slate-400"> — bahagian {s.bahagian.ke}/{s.bahagian.jumlah}</span>}
                    </li>
                  ))}
                </ol>
              </div>

              <div className="flex justify-end mb-2">
                <button onClick={klikSemua} className="text-[12px] font-semibold text-indigo-600 hover:underline">{salinSemua || 'Copy semua (system + user)'}</button>
              </div>
              <Blok tajuk="Instructions (system message)" teks={k.system} lalaiBuka />
              <Blok tajuk="Input (user message) — parameter run + data produk" teks={k.user} lalaiBuka />
              <Blok tajuk="Output schema (Structured Outputs)" teks={JSON.stringify(k.skema, null, 2)} />
            </>
          )}
        </>
      )}

      <details className="rounded-xl bg-white border border-slate-200 p-4">
        <summary className="text-[13.5px] font-semibold text-slate-800 cursor-pointer">Conflict-resolution policy (bila lapisan bercanggah)</summary>
        <ol className="m-0 mt-2 pl-5 text-[12.5px] text-slate-600 leading-relaxed list-decimal">
          {(dasar || []).map((x, i) => <li key={i}>{x}</li>)}
        </ol>
      </details>
    </div>
  );
}
